import { Loader, Pill } from '@mantine/core';
import { Activity, Calendar, Clipboard, ClipboardList, Clock, Hash } from 'lucide-react';
import { FC } from 'react';

import { AppointmentPill, getCollectionPillColor } from '@/components/appointment-pill.tsx';
import { FormsStatusIcon } from '@/components/forms/forms-status-icon.tsx';

import { DailyAppointment, Insurance, Priority } from '@/models/appointment/Appointment.ts';
import FormattedForm from '@/models/forms/FormattedForm.ts';
import { FormsResolution } from '@/models/forms/forms-fetch.ts';

import { titleAfterId } from '@/utils/appointmentUtils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';
import { getPatientDisplayName } from '@/utils/patientUtils.ts';
import { cn } from '@/utils/styleUtils.ts';

import IconWithText from '../IconWithText.tsx';
import PriorityMarker from '../PriorityMarker.tsx';
import Paragraph from '../text/Paragraph.tsx';

interface Props {
    appointment: DailyAppointment;
    insurance?: Insurance;
    formsResolution: FormsResolution;
    formattedFormsForAppointment: FormattedForm[] | undefined;
    isTooltip?: boolean;
    isWhiteTextEnabled?: boolean;
    isLoadingForms?: boolean;
}

const AppointmentOverview: FC<Props> = ({
    appointment,
    insurance,
    formsResolution,
    formattedFormsForAppointment,
    isTooltip = false,
    isWhiteTextEnabled = false,
    isLoadingForms = false,
}) => {
    return (
        <div
            className={cn(
                'flex h-full flex-col justify-between gap-2',
                isTooltip && 'h-auto p-2',
                isWhiteTextEnabled && 'text-white'
            )}
        >
            <PriorityMarker priority={appointment.priority ?? Priority.NORMAL} />
            {isTooltip && (
                <div className="flex gap-2">
                    {appointment?.collection_data && (
                        <AppointmentPill
                            text={`${appointment.collection_data.position}/${appointment.collection_data.total_appointments}`}
                            color={getCollectionPillColor(appointment)}
                            variant="filled"
                        />
                    )}
                    <Paragraph className="text-sm font-medium">
                        {getPatientDisplayName(appointment) ?? 'Patient'}
                    </Paragraph>
                </div>
            )}

            <div className={cn('flex flex-col gap-1', isWhiteTextEnabled && '[&_p]:text-white [&_svg]:text-white')}>
                <IconWithText icon={Hash} text={appointment.patient?.account?.patient_number ?? '-'} />
                <IconWithText
                    icon={Calendar}
                    text={dayjs(appointment.patient?.account?.date_of_birth ?? '-').format('DD.MM.YYYY')}
                />
                <IconWithText
                    icon={Activity}
                    text={appointment?.name ? titleAfterId(appointment?.name) : 'Keine Behandlung'}
                />
                <IconWithText icon={Clipboard} text={appointment.description || 'Keine Bezeichnung verfügbar'} />
                <IconWithText icon={ClipboardList} text={appointment?.data?.info || 'Keine Informationen verfügbar'} />
            </div>

            <div
                // set up text to white when color of background is dark
                className={cn(
                    'flex items-center justify-between gap-2',
                    isWhiteTextEnabled && '[&_p]:text-white [&_svg]:text-white'
                )}
            >
                <IconWithText
                    icon={Clock}
                    text={`${dayjs(appointment?.start ?? '-').format('HH:mm')} - ${dayjs(appointment?.end ?? '-').format('HH:mm')}`}
                />
                <div className="flex items-center gap-2">
                    {insurance && (
                        <Pill
                            size="xs"
                            className={cn(
                                'bg-blue-100 text-blue-700',
                                insurance === Insurance.PUBLIC && 'bg-red-100 text-red-700',
                                appointment?.data?.is_insurance_card_active && 'bg-green-100 text-green-700'
                            )}
                        >
                            {insurance}
                        </Pill>
                    )}
                    {isLoadingForms ? (
                        <Loader size={24} className={cn(isWhiteTextEnabled ? 'text-white' : 'text-slate-500')} />
                    ) : (
                        <FormsStatusIcon
                            formsResolution={formsResolution}
                            formattedFormsForAppointment={formattedFormsForAppointment}
                            colorClassName={isWhiteTextEnabled ? 'text-white' : undefined}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default AppointmentOverview;
